import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';

// Configuration de l'instance Axios
const api = axios.create({
    baseURL: 'https://api.blackroq.pro/api'
});

const InvestmentConfirmation = () => {
    const navigate = useNavigate();
    const location = useLocation(); 
    const query = new URLSearchParams(location.search);

    const planRaw = query.get('plan');
    const amountRaw = query.get('amount');
    const referralCode = query.get('referral'); 
    
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');

    // --- LOGIQUE DE RÉCUPÉRATION DU RÔLE DEPUIS MONGODB (VIA LOCALSTORAGE) ---
    const userString = localStorage.getItem('user');
    const userData = userString ? JSON.parse(userString) : null;
    const isLeader = userData && userData.role === 'leader'; 
    // -----------------------------------------------------------------------
    
    let cleanedAmount = NaN;
    if (amountRaw) {
        cleanedAmount = parseFloat(amountRaw.replace(/[^0-9.]/g, ''));
    }

    const formattedAmount = cleanedAmount && !isNaN(cleanedAmount) ? `${cleanedAmount.toLocaleString()} FCFA` : 'N/A';
    const planName = planRaw ? decodeURIComponent(planRaw) : 'N/A';
    
    const EXCHANGE_RATE_USDT_TO_XOF = 563.00;
    const amountInUSDT = (cleanedAmount / EXCHANGE_RATE_USDT_TO_XOF).toFixed(2);

    useEffect(() => {
        if (!planRaw || !amountRaw || isNaN(cleanedAmount) || cleanedAmount <= 0) {
            console.error("Paramètres d'investissement manquants ou invalides.");
            navigate('/dashboard');
        }

        // Si l'utilisateur est un leader, on affiche le message de restriction tout de suite
        if (isLeader) {
            setMessage("En tant que Leader, vous n'êtes plus éligible pour souscrire à un nouvel investissement.");
        }
    }, [planRaw, amountRaw, cleanedAmount, navigate, isLeader]);

    const handleConfirm = async () => {
        // Double sécurité : On bloque si le rôle est Leader
        if (isLeader) {
            setMessage("Action interdite : Les comptes Leaders ne peuvent plus investir.");
            return;
        }

        setLoading(true);
        setMessage('');
        const token = localStorage.getItem('token');
        
        if (!token) {
            setMessage('Votre session a expiré. Veuillez vous reconnecter.');
            setLoading(false);
            navigate('/login');
            return;
        }

        try {
            const numericAmount = Number(cleanedAmount);

            if (isNaN(numericAmount)) {
                setMessage("Erreur interne: Le montant n'est pas un nombre valide.");
                setLoading(false);
                return;
            }

            const investmentData = {
                plan: planName, 
                amount: numericAmount.toString(), 
            };
            
            if (referralCode) {
                investmentData.referralLink = referralCode;
            }

            const config = {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`, 
                },
            };

            let res;
            try {
                res = await api.post('/investment/invest', investmentData, config);
            } catch (firstError) {
                res = await api.post('/user/', investmentData, config);
            }

            setMessage(res.data.message || "Investissement confirmé avec succès !");
            
            setTimeout(() => {
                navigate('/dashboard', { state: { successMessage: res.data.message } });
            }, 2000);

        } catch (err) {
            setLoading(false);
            if (err.response) {
                const resData = err.response.data;
                setMessage(resData.message || "Une erreur est survenue.");
            } else {
                setMessage("Une erreur de réseau est survenue.");
            }
        }
    };

    const handleCancel = () => {
        navigate('/dashboard');
    };

    return (
        <div className="container mt-5">
            <div className="card shadow p-4 mx-auto" style={{ maxWidth: '500px' }}>
                <div className="text-center text-success mb-4">
                    <FontAwesomeIcon icon={faCheckCircle} size="5x" />
                </div>
                <h2 className="text-center mb-4">Confirmer votre investissement</h2>
                <div className="mb-4">
                    <p>
                        Vous êtes sur le point de souscrire au plan <strong>{planName}</strong>
                        pour un montant de <strong>{formattedAmount}</strong>.
                    </p>
                    {referralCode && (
                        <div className="alert alert-info mt-3">
                            Cette souscription est liée au lien de parrainage : <strong>{referralCode}</strong>
                        </div>
                    )}
                </div>

                {message && (
                    <div className={`mb-3 alert ${message.includes('succès') ? 'alert-success' : 'alert-danger'}`}>
                        {message}
                    </div>
                )}

                <div className="d-grid gap-2">
                    <button 
                        className="btn btn-success btn-lg" 
                        onClick={handleConfirm}
                        // Le bouton est grisé si c'est un Leader
                        disabled={loading || isLeader} 
                    >
                        {loading ? 'Confirmation en cours...' : 'Confirmer la souscription'}
                    </button>
                    <button className="btn btn-secondary btn-lg" onClick={handleCancel}>
                        Annuler
                    </button>
                </div>
            </div>
        </div>
    );
};

export default InvestmentConfirmation;